export type VariantCarryChoice = {
  media: boolean;
  taxonomy: boolean;
};

export type VariantCarryRow = {
  key: string;
  title: string;
  mediaCount: number;
  hasTaxonomy: boolean;
};

const LEGACY_STEPS = [
  { key: "designing", title: "Designing", detailKey: "designingDetails" },
  { key: "weaving", title: "Weaving", detailKey: "weavingDetails" },
  { key: "dying", title: "Dyeing", detailKey: "dyingDetails" },
  { key: "finishing", title: "Finishing", detailKey: "finishingDetails" },
];

function isFlexible(product: any): boolean {
  if (String(product?.processWorkflowVersion || "").toLowerCase() === "flexible") {
    return true;
  }
  return Array.isArray(product?.flexibleUploadProcesses) && product.flexibleUploadProcesses.length > 0;
}

function countMedia(media: unknown): number {
  if (!Array.isArray(media)) {
    return 0;
  }
  return media.filter((item) => {
    const url = String(item?.mediaURL || item?.mediaUrl || "").trim();
    return url.length > 0;
  }).length;
}

function hasTaxonomy(values: unknown): boolean {
  if (!values || typeof values !== "object" || Array.isArray(values)) {
    return false;
  }
  return Object.values(values as Record<string, unknown>).some((value) => {
    if (value == null) {
      return false;
    }
    if (typeof value === "string") {
      return value.trim().length > 0;
    }
    if (typeof value === "number") {
      return !Number.isNaN(value);
    }
    if (typeof value === "boolean") {
      return true;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    if (typeof value === "object") {
      const row = value as { value?: unknown; label?: unknown; id?: unknown; _id?: unknown };
      return Boolean(row.value || row.label || row.id || row._id);
    }
    return false;
  });
}

function flexibleKey(process: any, index: number): string {
  const productProcessId = String(process?.productProcessId || "").trim();
  return productProcessId || `flexible-${index}`;
}

export function variantCarryRows(product: any): VariantCarryRow[] {
  if (!product) {
    return [];
  }
  if (isFlexible(product)) {
    const processes = Array.isArray(product.flexibleUploadProcesses)
      ? product.flexibleUploadProcesses
      : [];
    return processes.map((process: any, index: number) => ({
      key: flexibleKey(process, index),
      title: String(process?.title || "").trim() || `Process ${index + 1}`,
      mediaCount: countMedia(process?.media),
      hasTaxonomy: hasTaxonomy(process?.taxonomyValues),
    }));
  }
  return LEGACY_STEPS.flatMap((step) => {
    const detail = product?.[step.detailKey];
    if (!detail) {
      return [];
    }
    const row = {
      key: step.key,
      title: step.title,
      mediaCount: countMedia(detail?.media),
      hasTaxonomy: hasTaxonomy(detail?.taxonomyValues),
    };
    const hasArtisan = Boolean(detail?.karigar?.name);
    if (row.mediaCount === 0 && !row.hasTaxonomy && !hasArtisan) {
      return [];
    }
    return [row];
  });
}

function choiceFor(choices: Record<string, VariantCarryChoice> | undefined, key: string): VariantCarryChoice {
  return choices?.[key] || { media: false, taxonomy: false };
}

export function applyVariantCarry(
  product: any,
  choices: Record<string, VariantCarryChoice>
): any {
  if (!product) {
    return product;
  }
  const next = JSON.parse(JSON.stringify(product));
  if (isFlexible(next)) {
    const processes = Array.isArray(next.flexibleUploadProcesses) ? next.flexibleUploadProcesses : [];
    next.flexibleUploadProcesses = processes.map((process: any, index: number) => {
      const choice = choiceFor(choices, flexibleKey(process, index));
      return {
        ...process,
        media: choice.media ? process.media || [] : [],
        taxonomyValues: choice.taxonomy ? process.taxonomyValues || {} : {},
      };
    });
    return next;
  }
  LEGACY_STEPS.forEach((step) => {
    const detail = next[step.detailKey];
    if (!detail) {
      return;
    }
    const choice = choiceFor(choices, step.key);
    next[step.detailKey] = {
      ...detail,
      media: choice.media ? detail.media || [] : [],
      taxonomyValues: choice.taxonomy ? detail.taxonomyValues || {} : {},
    };
  });
  return next;
}
